import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { selectStyle } from "@repo/ui/Select/style";
import { IconSelector } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { Button, ListBox, ListBoxItem, Select } from "react-aria-components";
import { useTrainingParticipantLessonAttemptsQuery } from "../../api/entities/trainingParticipant.js";

const CURRENT_ATTEMPT_KEY = "_current";

interface LessonAttemptSelectProps {
  projectId: string;
  participantId: string;
  interviewSectionDefinitionId: string;
  /** `null` means "current attempt" (most recently completed). */
  value: string | null;
  onChange: (value: string | null) => void;
}

interface AttemptOption {
  key: string;
  /** `null` represents the "current attempt" option. */
  resultId: string | null;
  title: string;
  subtitle: string;
}

export const LessonAttemptSelect: FC<LessonAttemptSelectProps> = (props) => {
  const { data: attempts } = useTrainingParticipantLessonAttemptsQuery({
    projectId: props.projectId,
    participantId: props.participantId,
    interviewSectionDefinitionId: props.interviewSectionDefinitionId,
  });

  const options = useMemo<readonly AttemptOption[]>(() => {
    if (!attempts || attempts.length === 0) {
      return [];
    }

    const [currentAttempt, ...previousAttempts] = attempts;
    if (!currentAttempt) {
      return [];
    }

    const currentTime = DateTime.fromISO(currentAttempt.started_at);

    return [
      {
        key: CURRENT_ATTEMPT_KEY,
        resultId: null,
        title: "Current attempt",
        subtitle: currentTime.toLocaleString(DateTime.TIME_SIMPLE),
      },
      ...previousAttempts.map((attempt): AttemptOption => {
        const time = DateTime.fromISO(attempt.started_at);
        return {
          key: attempt.id,
          resultId: attempt.id,
          title: time.toLocaleString(DateTime.DATE_MED),
          subtitle: time.toLocaleString(DateTime.TIME_SIMPLE),
        };
      }),
    ];
  }, [attempts]);

  if (options.length <= 1) {
    return null;
  }

  const selectedKey = props.value ?? CURRENT_ATTEMPT_KEY;
  const selectedOption = options.find((o) => o.key === selectedKey);

  return (
    <Select
      aria-label="Attempt"
      value={selectedKey}
      onChange={(key) => {
        const option = options.find((o) => o.key === key);
        if (!option) {
          return;
        }
        props.onChange(option.resultId);
      }}
    >
      <Button className={selectStyle().button({ className: "shrink-0" })}>
        <div className={selectStyle().value()}>
          {selectedOption?.title ?? "Current attempt"}
        </div>
        <IconSelector className={selectStyle().chevron()} />
      </Button>
      <SimpleMenuPopover placement="bottom end">
        <ListBox items={options}>
          {(option) => (
            <ListBoxItem
              id={option.key}
              textValue={`${option.title} ${option.subtitle}`}
              className={menuStyle().item({ className: "py-1.5" })}
            >
              <div className="flex flex-col leading-tight">
                <span className="truncate text-sm group-selected:font-semibold">
                  {option.title}
                </span>
                <span className="truncate text-xs font-normal text-muted-foreground group-focus:text-white">
                  {option.subtitle}
                </span>
              </div>
            </ListBoxItem>
          )}
        </ListBox>
      </SimpleMenuPopover>
    </Select>
  );
};
